import React, { useEffect, useRef, useState } from 'react'
import useSupercluster from 'use-supercluster'

import Garages from './garages'
import {
    applySearchFallbackViewport,
    applySearchNearestGarageViewport,
    isValidHoverPoint,
    superClusterOptions,
} from './helpers'
import type {
    ClusterAggregateProperties,
    ClusterFeature,
    GaragePointFeature,
    GaragePointProperties,
    MapProps,
    MapSupercluster,
} from './types'

const PRAGUE_BOUNDS: google.maps.LatLngBoundsLiteral = {
    south: 50.02,
    west: 14.32,
    north: 50.13,
    east: 14.52,
}

const INITIAL_ZOOM = 12

export default function CustomMap({
    garageList,
    isActive,
    garagesAvailableLater,
    selectedId,
    setSelectedId,
    hoverPoint,
    setHoverPoint,
    searchLocationPoint,
    loadingGarageAvailability,
    currency,
    pwa,
    navigateToGarage,
}: MapProps) {
    const [map, setMap] = useState<google.maps.Map | null>(null)
    const ref = useRef<HTMLDivElement | null>(null)
    const lastAppliedSearchKey = useRef<string | null | undefined>(undefined)
    const pendingSearchKey = useRef<string | null>(null)

    const mapOptions: google.maps.MapOptions = {
        zoom: INITIAL_ZOOM,
        center: {
            lat: (PRAGUE_BOUNDS.south + PRAGUE_BOUNDS.north) / 2,
            lng: (PRAGUE_BOUNDS.west + PRAGUE_BOUNDS.east) / 2,
        },
        restriction: {
            latLngBounds: {
                north: 58,
                south: 40,
                east: 30,
                west: 0,
            },
        },
        mapId: 'de111154cdacd3eb',
        gestureHandling: 'greedy',
        fullscreenControl: !pwa,
        streetViewControl: !pwa,
        streetViewControlOptions: {
            position: 1.0, // LEFT_TOP
        },
        zoomControl: !pwa,
        zoomControlOptions: {
            position: 7.0, // RIGHT_TOP
        },
        mapTypeControl: false,
        clickableIcons: false,
    }

    const searchLocationKey = isValidHoverPoint(searchLocationPoint)
        ? `${searchLocationPoint.lat},${searchLocationPoint.lng}`
        : null

    // const [mapBounds, setMapBounds] = useState<Array<number>>([-180, -90, 180, 90])
    const [zoom, setZoom] = useState(INITIAL_ZOOM)

    const points = garageList.map((garage): GaragePointFeature => {
        const laterAvailability = garagesAvailableLater?.garages.find(
            item => item.garageId === garage.id,
        )

        return {
            type: 'Feature',
            properties: {
                cluster: false,
                garage: {
                    ...garage,
                    availableLaterAt: laterAvailability?.earliestAvailableTime ?? null,
                },
            },
            geometry: {
                type: 'Point',
                coordinates: [
                    garage.lng,
                    garage.lat,
                ],
            },
        }
    })

    const { clusters, supercluster } = useSupercluster<
        GaragePointProperties,
        ClusterAggregateProperties
    >({
        points,
        bounds: [
            -180,
            -90,
            180,
            90,
        ],
        //bounds: mapBounds, // Can be set for current mapBounds but due to a high number of garages it is easier to avoid rerenders and calculate for whole map
        zoom,
        options: superClusterOptions,
    })
    const typedClusters = clusters as Array<ClusterFeature>
    const typedSupercluster = supercluster as MapSupercluster | undefined

    // biome-ignore lint: useEffect deps
    useEffect(() => {
        if (!ref.current) return
        setMap(new window.google.maps.Map(ref.current, mapOptions))
    }, [])

    // biome-ignore lint: useEffect deps
    useEffect(() => {
        if (!map) return

        const listeners: Array<google.maps.MapsEventListener> = []
        listeners.push(
            google.maps.event.addListener(map, 'bounds_changed', () => {
                // This can be used to limit the supercluster calculation to the current map bounds
                // const currentBounds = map.getBounds()
                // const ne = currentBounds?.getNorthEast()
                // const sw = currentBounds?.getSouthWest()
                // setMapBounds([sw.lng(), sw.lat(), ne.lng(), ne.lat()])
                const currentZoom = map.getZoom()
                if (typeof currentZoom === 'number') {
                    setZoom(currentZoom)
                }
            }),
        )

        // Zoom management
        if (garageList.length <= 1) {
            listeners.push(
                google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                    // Set a minimum zoom level for initial bounds fit
                    const currentZoom = map.getZoom()
                    if (typeof currentZoom === 'number' && currentZoom > 13) {
                        map.setZoom(13)
                    }
                }),
            )
        }

        // Removing marker selection upon click on map
        listeners.push(
            google.maps.event.addListener(map, 'click', () => setSelectedId(null)),
        )
        // google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        //     // Move zoom up by one to create more space
        //     // Unfortunately this zooms out on every map tab mount on mobile
        //     map.setZoom(map.getZoom() - 1)
        // })

        const transitLayer = new google.maps.TransitLayer()
        transitLayer.setMap(map)

        return () => {
            for (const listener of listeners) {
                listener.remove()
            }
            transitLayer.setMap(null)
        }
    }, [map])

    // biome-ignore lint: useEffect deps
    useEffect(() => {
        if (!map || !isActive) return
        if (searchLocationKey === lastAppliedSearchKey.current) return

        lastAppliedSearchKey.current = searchLocationKey
        pendingSearchKey.current = null

        if (!searchLocationKey || !isValidHoverPoint(searchLocationPoint)) {
            map.fitBounds(PRAGUE_BOUNDS, 0)
            return
        }

        if (garageList.length === 0) {
            applySearchFallbackViewport(map, searchLocationPoint)
            pendingSearchKey.current = searchLocationKey
            return
        }

        applySearchNearestGarageViewport(map, searchLocationPoint, garageList)
    }, [map, isActive, searchLocationKey])

    // biome-ignore lint: useEffect deps
    useEffect(() => {
        if (!map || !isActive) return
        if (!searchLocationKey) return
        if (!isValidHoverPoint(searchLocationPoint)) return
        if (pendingSearchKey.current !== searchLocationKey) return
        if (garageList.length === 0) return

        applySearchNearestGarageViewport(map, searchLocationPoint, garageList)
        pendingSearchKey.current = null
    }, [map, isActive, searchLocationKey, garageList.length, searchLocationPoint?.lat, searchLocationPoint?.lng])

    // biome-ignore lint: useEffect deps
    useEffect(() => {
        if (selectedId?.fromList && map) {
            map.setZoom(16)
            map.panTo({ lat: selectedId.lat, lng: selectedId.lng })
        }
    }, [selectedId])

    const containerStyle = {
        flexGrow: 1,
    }

    return (
        <>
            <div ref={ref} id="map" style={containerStyle} />
            {map && isActive && typedSupercluster &&
                <Garages
                    map={map}
                    clusters={typedClusters}
                    supercluster={typedSupercluster}
                    selectedId={selectedId}
                    setSelectedId={setSelectedId}
                    hoverPoint={hoverPoint}
                    setHoverPoint={setHoverPoint}
                    searchLocationPoint={searchLocationPoint}
                    loadingGarageAvailability={loadingGarageAvailability}
                    currency={currency}
                    navigateToGarage={navigateToGarage}
                />
            }
        </>
    )
}
